import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import usePagination from "../../../../Components/Hooks/usePagination";
import { vendorInvoiceAPI } from "../../../../helpers/api/vendorInvoice";
import { selectPhases } from "../../../../slices/phases/selectors";
import { selectCostTypes } from "../../../../slices/vendorInvoiceConfig/selectors";
import { getPhases } from "../../../../slices/thunks";
import { fetchCostTypesAndCategories } from "../../../../slices/vendorInvoiceConfig/thunk";
import { buildApiParams, applyFiltersToParams, deepFiltersFromParams } from "../../utils/filters";

export default function useVendorDetail(cif) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const phases    = useSelector(selectPhases);
  const costTypes = useSelector(selectCostTypes);

  const [invoices,   setInvoices]   = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [filters,    setFiltersState] = useState(() => deepFiltersFromParams(searchParams));
  const [stats,      setStats]      = useState(null);
  const [vendorName, setVendorName] = useState(location.state?.vendorName || "");

  const filtersRef = useRef(filters);
  const pageRef    = useRef(parseInt(searchParams.get("page") || "1", 10));

  const fetchInvoices = useCallback(async (page, f) => {
    setLoading(true);
    try {
      const res = await vendorInvoiceAPI.getAll({ ...buildApiParams(page, f), cif });
      setInvoices(res.results ?? []);
      setPagination(res.pagination ?? null);
      if (!vendorName && res.results?.length > 0) setVendorName(res.results[0].name || "");
    } catch {
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  }, [cif]);

  const onPageChange = useCallback((page) => {
    pageRef.current = page;
    fetchInvoices(page, filtersRef.current);
    setSearchParams(applyFiltersToParams(filtersRef.current, page));
  }, [fetchInvoices, setSearchParams]);

  const { currentPage, nextPage, prevPage, resetPage } = usePagination(onPageChange, pageRef.current);

  useEffect(() => {
    fetchInvoices(pageRef.current, filtersRef.current);
    dispatch(getPhases());
    dispatch(fetchCostTypesAndCategories());

    vendorInvoiceAPI.getAll({ cif, page_size: 500 }).then((res) => {
      const all = res.results ?? [];
      const byStatus = all.reduce((acc, inv) => {
        acc[inv.status] = (acc[inv.status] || 0) + 1;
        return acc;
      }, {});
      const byAmount = all.reduce((acc, inv) => {
        const cur = inv.currency || "RON";
        acc[cur] = (acc[cur] || 0) + parseFloat(inv.total_amount || 0);
        return acc;
      }, {});
      const unique = (arr) => [...new Set(arr.filter(Boolean))];
      const costTypeNames  = unique(all.map((inv) => inv.cost_type_detail?.name  || inv.cost_type_name));
      const categoryNames  = unique(all.map((inv) => inv.category_detail?.name   || inv.category_name));
      const subCatNames    = unique(all.map((inv) => inv.sub_category_detail?.name || inv.sub_category_name));
      const blockNames     = unique(all.map((inv) => inv.block_detail?.name      || inv.block_name));

      if (!vendorName && all.length > 0) setVendorName(all[0].name || "");
      setStats({
        total: res.pagination?.count ?? all.length,
        byStatus, byAmount,
        costTypes:     costTypeNames,
        categories:    categoryNames,
        subCategories: subCatNames,
        blocks:        blockNames,
      });
    }).catch(() => {});
  }, []);

  const setFilters = useCallback((newFilters) => {
    filtersRef.current = newFilters;
    setFiltersState(newFilters);
    setSearchParams(applyFiltersToParams(newFilters, 1));
    resetPage();
  }, [resetPage, setSearchParams]);

  const refresh = useCallback(() => {
    fetchInvoices(pageRef.current, filtersRef.current);
  }, [fetchInvoices]);

  return {
    invoices, pagination, loading,
    phases, costTypes,
    filters, setFilters,
    currentPage, nextPage, prevPage,
    vendorName, stats,
    refresh,
  };
}
